import React, { useState } from 'react';
import { Lock, Mail, User } from 'lucide-react';
import {
  LoginBackButton,
  LoginEyeToggle,
  LoginInputRow,
  LoginLabel,
  LoginSubmit,
  LoginTextLink,
} from './LoginControls';

/**
 * Login v2 — password + create-password steps (TASK-201). Not in the mock
 * (it shows the email step); rendered in the same v2 card language so the
 * existing Firebase flows keep working unchanged.
 */

interface PasswordStepProps {
  email: string;
  password: string;
  setPassword: (value: string) => void;
  isLoading: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onBack: () => void;
  onForgotPassword: () => void;
  /** A reset link request is in flight. */
  forgotSending: boolean;
}

export const PasswordStep: React.FC<PasswordStepProps> = ({
  email,
  password,
  setPassword,
  isLoading,
  onSubmit,
  onBack,
  onForgotPassword,
  forgotSending,
}) => {
  const [shown, setShown] = useState(false);
  return (
    <form onSubmit={onSubmit} className="flex flex-col">
      <LoginLabel htmlFor="login-email-display">Email address</LoginLabel>
      <LoginInputRow
        id="login-email-display"
        icon={<Mail />}
        type="email"
        value={email}
        disabled
        className="mb-[16px]"
      />
      <LoginLabel htmlFor="login-password">Password</LoginLabel>
      <LoginInputRow
        id="login-password"
        icon={<Lock />}
        type={shown ? 'text' : 'password'}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="Enter your password"
        autoComplete="current-password"
        required
        autoFocus
        trailing={<LoginEyeToggle shown={shown} onToggle={() => setShown(!shown)} />}
        className="mb-[12px]"
      />
      <div className="flex items-start justify-between gap-s3">
        <LoginBackButton onClick={onBack} />
        <LoginTextLink onClick={onForgotPassword} disabled={forgotSending || isLoading}>
          {forgotSending ? 'Sending link…' : 'Forgot password?'}
        </LoginTextLink>
      </div>
      <LoginSubmit loading={isLoading}>Sign in</LoginSubmit>
    </form>
  );
};

interface CreatePasswordStepProps {
  name: string;
  setName: (value: string) => void;
  password: string;
  setPassword: (value: string) => void;
  confirmPassword: string;
  setConfirmPassword: (value: string) => void;
  isLoading: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onBack: () => void;
}

export const CreatePasswordStep: React.FC<CreatePasswordStepProps> = ({
  name,
  setName,
  password,
  setPassword,
  confirmPassword,
  setConfirmPassword,
  isLoading,
  onSubmit,
  onBack,
}) => {
  const [shown, setShown] = useState(false);
  const [confirmShown, setConfirmShown] = useState(false);
  return (
    <form onSubmit={onSubmit} className="flex flex-col">
      <LoginLabel htmlFor="login-name">Full name</LoginLabel>
      <LoginInputRow
        id="login-name"
        icon={<User />}
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Your full name"
        autoComplete="name"
        required
        autoFocus
        className="mb-[16px]"
      />
      <LoginLabel htmlFor="login-new-password">Create password</LoginLabel>
      <LoginInputRow
        id="login-new-password"
        icon={<Lock />}
        type={shown ? 'text' : 'password'}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="At least 8 characters"
        autoComplete="new-password"
        required
        trailing={<LoginEyeToggle shown={shown} onToggle={() => setShown(!shown)} />}
        className="mb-[16px]"
      />
      <LoginLabel htmlFor="login-confirm-password">Confirm password</LoginLabel>
      <LoginInputRow
        id="login-confirm-password"
        icon={<Lock />}
        type={confirmShown ? 'text' : 'password'}
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        placeholder="Confirm password"
        autoComplete="new-password"
        required
        trailing={<LoginEyeToggle shown={confirmShown} onToggle={() => setConfirmShown(!confirmShown)} />}
        className="mb-[12px]"
      />
      <LoginBackButton onClick={onBack} />
      <LoginSubmit loading={isLoading}>Create account</LoginSubmit>
    </form>
  );
};
